'use client';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { Numeric } from '@/components/ui/numeric';
import { Badge } from '@/components/ui/badge';
import type { JourneyPool } from '../../libs/playerJourney';
import { eventPlayerHref, type EventPlayer } from '../../libs/eventPlayers';
import { NAME_WRAP } from '../../libs/nameBreaks';
import type { JourneyContext } from './JourneyFixtureCard';
import { NameText } from './NameText';

export function PlayerJourneyPool({
  pool,
  opponents,
  playerId,
  division,
  drawHref,
  ...context
}: JourneyContext & {
  pool: JourneyPool;
  opponents: EventPlayer[];
  playerId: string;
  division: string;
  /** The division's draw page: the pool card is the way back to it. */
  drawHref?: string;
}) {
  const t = useTranslations('Event.playerJourney');
  const rows = pool.rows.flatMap((row) => {
    const player = opponents.find((p) => p.playerId === row.playerId);
    return player ? [{ ...row, player }] : [];
  });
  return (
    <section
      className="bg-card border-border min-w-0 rounded-xl border p-4"
      aria-label={t('poolTable', { stage: pool.stageName })}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-heading font-semibold">{pool.stageName}</h2>
        <Badge variant="outline">{t(pool.final ? 'finished' : 'playing')}</Badge>
      </div>
      <table className="mt-3 w-full table-fixed text-xs">
        <caption className="sr-only">{t('poolTable', { stage: pool.stageName })}</caption>
        <thead>
          <tr className="border-border text-muted border-b">
            <th className="w-[10%] py-2 text-left" scope="col">
              {t('rankShort')}
            </th>
            <th className="w-[54%] text-left" scope="col">
              {t('playerColumn')}
            </th>
            {['playedColumn', 'winsColumn', 'lossesColumn'].map((key) => (
              <th key={key} scope="col">
                <abbr className="no-underline" title={t(key)}>
                  {t(`${key}Short`)}
                </abbr>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.playerId}
              className={`border-border border-b ${row.playerId === playerId ? 'bg-primary/10 font-semibold' : ''}`}
            >
              <td className="py-2">
                <Numeric>{row.position}</Numeric>
              </td>
              <th scope="row" className={`text-left font-medium ${NAME_WRAP}`}>
                <Link
                  className="inline-flex min-h-11 max-w-full items-center underline-offset-4 hover:underline"
                  href={`/${context.locale}${eventPlayerHref(context.eventId, context.slug, row.playerId, division)}`}
                >
                  <span className="min-w-0">
                    <NameText name={row.player.label} />
                  </span>
                </Link>
              </th>
              {[row.played, row.wins, row.losses].map((value, i) => (
                <td key={i} className="text-center">
                  <Numeric>{value}</Numeric>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {pool.qualification === 'notQualified' && (
        <p className="text-muted mt-3 text-xs">{t('placementUnknown')}</p>
      )}
      {pool.qualification && (
        <p className="text-muted mt-3 text-xs">
          {t(
            pool.qualification === 'qualified'
              ? 'qualificationConfirmed'
              : pool.qualification === 'notQualified'
                ? 'qualificationNotAdvanced'
                : 'qualificationPending',
          )}
        </p>
      )}
      {drawHref && (
        <div className="border-border mt-3 border-t pt-1">
          <Link
            href={drawHref}
            className="group text-foreground hover:bg-surface focus-visible:ring-primary -mx-2 flex min-h-11 items-center justify-between gap-2 rounded-md px-2 text-sm font-semibold transition-colors focus-visible:ring-2 focus-visible:outline-none"
          >
            {t('viewDraw')}
            <ChevronRightIcon
              aria-hidden="true"
              className="text-muted group-hover:text-foreground size-4 shrink-0 transition-transform group-hover:translate-x-0.5"
            />
          </Link>
        </div>
      )}
    </section>
  );
}
